import React from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  schemaType?: 'WebPage' | 'MedicalBusiness' | 'Product' | 'Article';
  schemaData?: any;
}

export default function SEOOptimized({ 
  title = "Nirvana Ayurveda - Natural Health Solutions",
  description = "Leading Ayurveda clinic offering natural health solutions for weight management, joint pain relief, and holistic wellness. Expert Ayurvedic treatments with proven results.",
  keywords = "ayurveda, ayurvedic treatment, natural health, holistic wellness, weight management, joint pain relief, herbal medicine, ayurvedic doctor, natural remedies, wellness center",
  canonical = "https://nirvanaayurveda.infy.uk",
  schemaType = "WebPage",
  schemaData = {}
}: SEOProps) {

  React.useEffect(() => {
    // Update page title
    document.title = title;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', keywords);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);

    // Update Open Graph tags
    const updateOGTag = (property: string, content: string) => {
      let ogTag = document.querySelector(`meta[property="${property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', content);
    };

    updateOGTag('og:title', title);
    updateOGTag('og:description', description);
    updateOGTag('og:url', canonical);

    // Update Twitter tags
    const updateTwitterTag = (name: string, content: string) => {
      let twitterTag = document.querySelector(`meta[name="${name}"]`);
      if (!twitterTag) {
        twitterTag = document.createElement('meta');
        twitterTag.setAttribute('name', name);
        document.head.appendChild(twitterTag);
      }
      twitterTag.setAttribute('content', content);
    };

    updateTwitterTag('twitter:title', title);
    updateTwitterTag('twitter:description', description);

    // Add structured data if provided
    if (schemaData && Object.keys(schemaData).length > 0) {
      const schema = {
        "@context": "https://schema.org",
        "@type": schemaType,
        "name": title,
        "description": description,
        "url": canonical,
        ...schemaData
      };

      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.innerHTML = JSON.stringify(schema, null, 2);
    }
  }, [title, description, keywords, canonical, schemaType, schemaData]);

  return null; // This component doesn't render anything
}

// Local Business Schema for Ayurveda Clinic
export const localBusinessSchema = {
  "@type": ["MedicalOrganization", "LocalBusiness"],
  "name": "Nirvana Ayurveda",
  "description": "Leading Ayurveda clinic offering natural health solutions for weight management, joint pain relief, and holistic wellness.",
  "url": "https://nirvanaayurveda.infy.uk",
  "logo": "https://nirvanaayurveda.infy.uk/ayur.jpg",
  "image": "https://nirvanaayurveda.infy.uk/ayur.jpg",
  "medicalSpecialty": [
    "Ayurveda",
    "Natural Medicine", 
    "Holistic Health",
    "Weight Management",
    "Joint Pain Treatment",
    "Herbal Medicine",
    "Wellness Consultation"
  ],
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "UK",
    "addressLocality": "United Kingdom"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "51.5074",
    "longitude": "0.1278"
  },
  "openingHours": [
    "Mo-Fr 09:00-18:00",
    "Sa 09:00-16:00"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Ayurvedic Treatments & Products",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalProcedure",
          "name": "Ayurvedic Weight Management Program",
          "description": "Natural weight loss solutions using traditional Ayurvedic methods and herbs"
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "MedicalProcedure",
          "name": "Joint Pain Relief Treatment",
          "description": "Natural joint pain relief using Ayurvedic herbs, oils and therapeutic methods"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalProcedure", 
          "name": "Holistic Wellness Consultation",
          "description": "Comprehensive Ayurvedic health assessment and personalized treatment plan"
        }
      }
    ]
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "127",
    "bestRating": "5",
    "worstRating": "1"
  }
};
